import React, { FC, useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { EmptyIcon } from '../../assets/svg/emptyIcon';
import { ExternalIcon } from '../../assets/svg/ExternalIcon';
import { RefreshIcon } from '../../assets/svg/refreshIcon';
import { useAgent } from '../../newrelic';
import { WidgetErrors, PaymentLabels, WidgetId } from '../../shared/constants';

export type WidgetFailedProps = {
	redirectUrl?: string;
	paymentLabel?: string;
};

export const WidgetFailed: FC<WidgetFailedProps> = ({ redirectUrl, paymentLabel }) => {
	const agent = useAgent();
	const { t: translate } = useTranslation();
	const { VerbSentenceCase } = paymentLabel
		? PaymentLabels[(paymentLabel as any as string).toUpperCase() as keyof typeof PaymentLabels]
		: { VerbSentenceCase: 'Give' };

	useEffect(() => {
		agent?.recordCustomEvent(WidgetId, {
			type: WidgetErrors.failedToLoad,
			redirectUrl,
			host: window.location.host,
		});
	}, [agent, redirectUrl]);

	return (
		<div>
			<style>
				{` 
				.widget-failed,
				.widget-failed *,
				.widget-failed *::before,
				.widget-failed *::after { 
					box-sizing: border-box; 
				}

				.widget-failed {
					font-family: 'Inter', sans-serif;
					width: 400px;
					height: 600px;
					border-radius: 5px;
					margin: auto;
					padding: 24px;
					background-color: #ffffff;
					box-shadow: 0 0px 10px 0px rgba(0, 0, 0, 0.1);
					display: flex;
					flex-direction: column;
					justify-content: center;
					align-items: center;
				}

				.widget-failed button {
					display: flex;
					justify-content: center;
					align-items: center;
					border: 1px solid #006A6A;
					background-color: transparent;
					outline: none;
					border-radius: 48px;
					color: #006A6A;
					cursor: pointer;
					font-size: 16px;
					font-weight: 600;
					height: 48px;
					padding: 8px 24px;
					text-align: center;
				}

				.widget-failed h5 {
					padding-top: 24px;
					padding-bottom: 36px;
					margin: 0;
					border-radius: 4px;
					font-weight: 600;
					font-size: 20px;
					line-height: 32px;
					letter-spacing: 0px;
					text-align: center;
				}

				.widget-failed p {
					font-weight: 400;
					font-size: 16px;
					text-align: center;
					margin-bottom: 24px;
				}

				.widget-failed button span {
					padding-left: 8px;
				}

				.widget-failed a {
					text-decoration: none;
					display: flex;
					justify-content: center;
					align-items: center;
					background-color: transparent;
					outline: none;
					color: #006A6A;
					cursor: pointer;
					font-size: 16px;
					font-weight: 600;
					padding: 8px 24px;
					text-align: center;
					margin-bottom: 24px;
					border: 1px solid #006A6A;
					background-color: transparent;
					outline: none;
					border-radius: 48px;
				}

				@media only screen and (max-width: 600px) {
					.widget-failed {
						width: 100%;
					}
				}
			`}
			</style>
			<div className="widget-failed">
				<EmptyIcon width="151" />
				<h5>Oh no! Something has gone wrong</h5>
				<p>Sorry, we are not able to load this widget for you right now. Please try again later.</p>
				{redirectUrl && (
					<a href={redirectUrl}>
						<ExternalIcon width="30" />
						<span>
							{translate('errors.redirectToWebgiving', { label: VerbSentenceCase }) || 'Redirect to give'}
						</span>
					</a>
				)}
				<button type="button" onClick={() => window.location.reload()}>
					<RefreshIcon width="17" />
					<span>Refresh</span>
				</button>
			</div>
		</div>
	);
};

export default WidgetFailed;
